/**
 * The NotesAI-side RDS switch, as the browser sees it.
 *
 * "Use Supabase only" / "Enable RDS" in the SuperAdmin panel used to change
 * RDS's OWN source mode (POST /admin/source-mode on RDS) -- a global change
 * that stopped RDS for every NotesAI deployment, the mobile API and every
 * collab server at once. Since 2026-10-24 those buttons flip a switch that
 * belongs to THIS NotesAI deployment (backend/api/rds_local_switch.py):
 * RDS is never told, keeps running in whatever mode it is in, and only this
 * frontend + its Django backend stop using it.
 *
 * This module is that switch's client. rdsPhi.fetchCutoverFlags asks it
 * BEFORE asking RDS for its cutover flags; while the switch says "RDS
 * disabled here" rdsPhi answers the payload a paused RDS would answer
 * (kill switch on, source mode supabase_only) and RDS is not contacted.
 *
 * Caching mirrors getCutoverFlags: the last good answer is reused for 30 s,
 * a failed lookup for 15 s, one request in flight per tab. The last answer
 * is also kept in localStorage for 24 h (`sticky`), so a backend blip
 * cannot flip a deliberately paused frontend back onto RDS -- the same rule
 * rdsPhi applies to the source mode. With no memory at all the answer is
 * "RDS enabled here": today's behaviour.
 *
 * Nothing here is PHI: a boolean, a timestamp, an operator id and a note.
 */
import { getSupabase } from "../components/supabaseClient";
import { getBackendURL } from "../components/env";

const STORAGE_KEY = "rds_local_switch";
const STICKY_TTL_MS = 24 * 60 * 60 * 1000;

export class LocalSwitchError extends Error {
  constructor(statusCode, detail) {
    super(`NotesAI backend returned ${statusCode}${detail ? `: ${detail}` : ""}`.slice(0, 500));
    this.statusCode = statusCode;
    this.detail = detail;
  }
}

function readSticky() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.disabled !== "boolean" || !Number.isFinite(parsed?.at)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

let sticky = readSticky(); // { disabled, at, server } | null
let cache = null; // { state, fetchedAt, failed } | null
let inflight = null;

function remember(state) {
  if (!state || typeof state.disabled !== "boolean") return;
  sticky = { disabled: state.disabled, at: Date.now(), server: state.server || null };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sticky));
  } catch (_) {
    // memory alone is fine for this tab
  }
}

function stickyState() {
  if (!sticky) return null;
  if (Date.now() - sticky.at > STICKY_TTL_MS) return null;
  return sticky;
}

/** Test hook -- forget the cache, the request in flight and the sticky value. */
export function _resetLocalRdsSwitch() {
  cache = null;
  inflight = null;
  sticky = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // nothing to forget
  }
}

/**
 * Is RDS disabled for this NotesAI, from memory only (no request)? True
 * when the last good answer (cache, else the sticky value) said so. Used
 * by rdsPhi to skip RDS entirely when the pause is already known.
 */
export function localRdsPausedSync() {
  if (cache && !cache.failed) return !!cache.state?.disabled;
  const s = stickyState();
  return !!s?.disabled;
}

/**
 * The switch's state: {disabled, since, by, note, source, server} from
 * GET /api/rds-local-switch/, cached. On ANY failure (no session, network,
 * timeout, non-2xx, non-JSON) the answer is {disabled: <sticky or false>,
 * error, stale: true} -- never throws.
 */
export async function getLocalRdsSwitch({
  maxAgeMs = 30000,
  failureMaxAgeMs = 15000,
  timeoutMs = 2500,
} = {}) {
  if (cache) {
    const age = Date.now() - cache.fetchedAt;
    if (age < (cache.failed ? failureMaxAgeMs : maxAgeMs)) return cache.state;
  }
  if (!inflight) {
    const req = fetchState(timeoutMs).finally(() => {
      if (inflight === req) inflight = null;
    });
    inflight = req;
  }
  return inflight;
}

async function fetchState(timeoutMs) {
  const failed = (error) => {
    const s = stickyState();
    const state = { disabled: !!s?.disabled, server: s?.server || null, error: String(error).slice(0, 200), stale: true };
    cache = { state, fetchedAt: Date.now(), failed: true };
    return state;
  };

  let token;
  try {
    const supabase = await getSupabase();
    const { data: { session } = {} } = await supabase.auth.getSession();
    token = session?.access_token;
  } catch (err) {
    return failed(err?.name || "session error");
  }
  if (!token) return failed("no session");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let resp;
    try {
      resp = await fetch(`${getBackendURL()}/api/rds-local-switch/`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        cache: "no-store",
      });
    } catch (err) {
      return failed(err?.name === "AbortError" ? "timeout" : err?.name || "fetch error");
    }
    if (!resp || !resp.ok) return failed(resp?.status ?? "no response");
    let state;
    try {
      state = await resp.json();
    } catch (_) {
      return failed("non-JSON response");
    }
    if (!state || typeof state !== "object" || typeof state.disabled !== "boolean") {
      return failed("malformed payload");
    }
    cache = { state, fetchedAt: Date.now(), failed: false };
    remember(state);
    return state;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Flip the switch for this NotesAI (POST /api/admin/rds-local-switch/,
 * admin only). Resolves the new state and updates the cache and the sticky
 * value at once, so this tab changes behaviour on its next read. The
 * caller (rdsPhi.setLocalRdsSwitch) also forgets the cached cutover flags.
 *
 * @throws {LocalSwitchError} non-2xx (401 session, 403 not an admin, 500
 *   could not write);
 *   {Error} no session or unreachable.
 */
export async function setLocalRdsSwitch(disabled, { note, timeoutMs = 8000 } = {}) {
  const supabase = await getSupabase();
  const { data: { session } = {} } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again.");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let resp;
    try {
      resp = await fetch(`${getBackendURL()}/api/admin/rds-local-switch/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ disabled: !!disabled, ...(note ? { note: String(note) } : {}) }),
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name === "AbortError") throw new Error("The NotesAI backend timed out.");
      throw new Error(`The NotesAI backend could not be reached: ${err.message}`);
    }
    let body = null;
    try {
      body = await resp.json();
    } catch (_) {
      body = null;
    }
    if (!resp.ok) throw new LocalSwitchError(resp.status, body?.error || body?.detail || "");
    const state = body?.local_switch;
    if (state && typeof state.disabled === "boolean") {
      cache = { state, fetchedAt: Date.now(), failed: false };
      remember(state);
    } else {
      cache = null;
    }
    return state || null;
  } finally {
    clearTimeout(timer);
  }
}
